import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import { triggerRerender } from '../../../redux/rerenderSlice';

export default function DeceasedModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { handleSubmit, control, reset, formState: { errors }, } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const [file, setFile] = useState(null);
  const active = modal.is_active && modal.type === 'deceased_modal'

  const resetState = () => {
    reset({ file: null })
    setFile(null)
    setSaving(false)
  }

  // This modal stays mounted in the Footer between openings, so a certificate picked for the
  // previous customer would otherwise still satisfy the required rule and be uploaded for this one.
  useEffect(() => {
    if (active) {
      resetState()
    }
  }, [active, customerId])

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }));
  };

  const onSubmit = async (formData) => {
    if (file) {
      setSaving(true)
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/deceased`)
      try {
        const response = await request.modify(formData, file)
        if (response.status === 200 || response.status === 201) {
          modal.options.successCallback()
          handleCancel()
          dispatch(triggerRerender({ subpanelId: 'document' }))
          dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        } else {
          modal.options.successCallback()
          setSaving(false)
          setFile(null)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        modal.options.successCallback()
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    } else {
      dispatch(setNotification({ type: 'error', body: "Please upload a file." }))
    }
  };

  return active ? (
    <form onSubmit={handleSubmit(onSubmit)} id="deceased_modal_form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card is-flex is-flex-direction-column column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">Customer Deceased</p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "110px", overflow: 'hidden' }}
          >
            <div className="columns mb-0 is-flex-direction-column">
              <div className="column">
                <h3 className="has-text-danger">IMPORTANT: This will change loan_status and make non-payable all outstanding payments.</h3>
              </div>
              <div className="column">
                <h3>Upload Death Certificate
                  <span className="has-text-danger">*</span>
                  <span
                    style={{ pointerEvents: 'auto' }}
                    className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                    data-tooltip={"Supported file types: .pdf, .jpg, .jpeg, .png, .doc, .docx"}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={`pl-1 has-text-link`}
                    />
                  </span>
                </h3>

                <div className="mt-1">
                  <Controller
                    control={control}
                    name="file"
                    rules={{ required: true }}
                    render={({ field: { value, onChange, ...field } }) => {
                      return (
                        <input
                          {...field}
                          value={value?.fileName}
                          onChange={(event) => {
                            onChange(event.target.files[0]);
                            setFile(event.target.files[0]);
                          }}
                          type="file"
                        />
                      );
                    }}
                  />
                </div>
                <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                  {errors.file && <span>{errors.file.message ? errors.file.message : "Certificate is required"}</span>}
                </p>
              </div>
              <SpecialInstruction
                content={"<span>" +
                  "<span class='has-text-weight-bold'>This will perform the following actions:</span><br>" +
                  "<span class='pl-2'>• Set customers.customer_status_dropdown_option_id = nonfundable</span><br> " +
                  "<span class='pl-2'>• Set nonfundable_reason_dropdown_option_id = Deceased</span><br> " +
                  "<span class='pl-2'>• Set customers.is_ssp_disabled = TRUE</span><br> " +
                  "<span class='pl-2'>• Set is_debit_card_disabled = TRUE</span><br> " +
                  "<span class='pl-2'>• Set is_consent_email_transactional = FALSE</span><br> " +
                  "<span class='pl-2'>• Set is_consent_email_marketing = FALSE</span><br> " +
                  "<span class='pl-2'>• Set is_consent_sms_transactional = FALSE</span><br> " +
                  "<span class='pl-2'>• Set is_consent_sms_marketing = FALSE</span><br> " +
                  "<span class='pl-2'>• Set is_consent_phone_marketing = FALSE</span><br> " +
                  "<span class='pl-2'>• Set is_do_not_call = TRUE</span><br> " +
                  "<span class='pl-2'>• Set is_home_phone_do_not_call = TRUE</span><br> " +
                  "<span class='pl-2'>• Set is_cell_phone_do_not_call = TRUE</span><br> " +
                  "<span class='pl-2'>• Set is_work_phone_do_not_call = TRUE</span><br> " +
                  "<span class='pl-2'>• Set all outstanding payments payment_method = None</span><br> " +
                  "<span class='pl-2'>• Set loan_status = loan_status_uncollectable</span><br> " +
                  "<span class='pl-2'>• Add customer SSN to OEL</span><br> " +
                  "<span>NOTE: This action is not easily undone.</span>" +
                  " </span>"}
                type="is-warning"
              />
            </div>
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Submitting' : 'Continue'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
